/**
 * CompaniesPage — company registry list (yritysrekisteri, #763/#764)
 *
 * Uses the shared list-page components and DataTable so the registry matches
 * the other Staff Portal entity lists (issue #880, epic #877).
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box, Button, IconButton, Tooltip, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { usePagedCompanies, type Company } from '@/hooks/useCompanies';
import { CompanyFormDialog } from '@/components/companies/CompanyFormDialog';
import { useDebounce } from '@/hooks/useDebounce';
import {
  DataTable,
  type DataTableColumn,
  EntityListResults,
  ListPageHeader,
  SearchField,
} from '@/components/common';

export default function CompaniesPage() {
  const { t } = useTranslation('companies');
  const navigate = useNavigate();

  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);
  const [dialogOpen, setDialogOpen] = useState(false);

  const {
    rows: companies,
    total,
    isLoading,
    isError,
    error,
    refetch,
    sort,
    setSort,
    pagination,
  } = usePagedCompanies(debouncedSearch ? { q: debouncedSearch } : {});

  const columns: DataTableColumn<Company>[] = [
    {
      header: t('table.name'),
      sortKey: 'name',
      renderCell: (company) => (
        <Typography variant="body2" fontWeight={500}>
          {company.name}
        </Typography>
      ),
    },
    {
      header: t('table.businessId'),
      sortKey: 'businessId',
      renderCell: (company) => <Typography variant="body2">{company.businessId ?? '—'}</Typography>,
    },
    {
      header: t('table.phone'),
      sortKey: 'phone',
      renderCell: (company) => <Typography variant="body2">{company.phone ?? '—'}</Typography>,
    },
    {
      header: t('table.email'),
      sortKey: 'email',
      renderCell: (company) => <Typography variant="body2">{company.email ?? '—'}</Typography>,
    },
    {
      header: '',
      align: 'center',
      width: 56,
      renderCell: (company) => (
        <Tooltip title={t('view')}>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/parties/companies/${company.id}`);
            }}
            aria-label={t('view')}
          >
            <ChevronRightIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  const renderMobileCard = (company: Company) => (
    <Box>
      <Typography variant="body2" fontWeight={600}>
        {company.name}
      </Typography>
      {company.businessId && (
        <Typography variant="body2" color="text.secondary">
          {company.businessId}
        </Typography>
      )}
      {company.phone && (
        <Typography variant="body2" color="text.secondary">
          {company.phone}
        </Typography>
      )}
      {company.email && (
        <Typography variant="body2" color="text.secondary">
          {company.email}
        </Typography>
      )}
    </Box>
  );

  return (
    <Box>
      <ListPageHeader
        title={t('title')}
        subtitle={t('subtitle')}
        actions={
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDialogOpen(true)}>
            {t('addCompany')}
          </Button>
        }
      />

      <SearchField value={search} onChange={setSearch} placeholder={t('searchPlaceholder')} />

      <EntityListResults
        loading={isLoading}
        isError={isError}
        errorText={t('errorLoading')}
        error={error}
        onRefetch={() => void refetch()}
        resultCountText={t('searchResultCount', { count: total })}
        pagination={pagination}
      >
        <DataTable<Company>
          columns={columns}
          data={companies}
          getRowKey={(company) => company.id}
          onRowClick={(company) => navigate(`/parties/companies/${company.id}`)}
          renderMobileCard={renderMobileCard}
          loading={isLoading}
          emptyMessage={t('empty')}
          emptyDescription={t('emptyDesc')}
          sortKey={sort?.sortBy ?? 'name'}
          sortDirection={sort?.sortDir ?? 'asc'}
          onSortChange={(sortKey, sortDirection) => {
            setSort({ sortBy: sortKey, sortDir: sortDirection });
          }}
          ariaLabel={t('title')}
        />
      </EntityListResults>

      <CompanyFormDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onCreated={(company) => navigate(`/parties/companies/${company.id}`)}
      />
    </Box>
  );
}
